import React, { useState, useEffect, useRef } from "react"
import Slider from "react-slick"
import { ChevronLeft, ChevronRight } from "lucide-react"

const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(false)

    useEffect(() => {
        const media = window.matchMedia(query)
        if (media.matches !== matches) {
            setMatches(media.matches)
        }
        const listener = () => {
            setMatches(media.matches)
        }
        media.addEventListener("change", listener)
        return () => media.removeEventListener("change", listener)
    }, [matches, query])

    return matches
}

const Card = ({ title }) => (
    <div className="h-full p-2">
        <div className="flex flex-col justify-center h-full p-10 text-center bg-white border border-gray-200 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold">{title}</h3>
        </div>
    </div>
)

const GridSlide = ({ items }) => (
    <div className="grid grid-cols-2 grid-rows-2 gap-0">
        {items.map((item) => (
            <Card key={item.id} title={item.title} />
        ))}
    </div>
)

const chunk = (arr, size) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
        arr.slice(i * size, i * size + size)
    )

export default function Carousel({
    items,
    itemTemplate,
    multipleRows = false,
}) {
    const ItemTemplate = itemTemplate
    const isDesktop = useMediaQuery("(min-width: 1040px)")
    const sliderRef = useRef(null)
    const [currentSlide, setCurrentSlide] = useState(0)

    // We need enough items for multiple 3-slide views. 24 items = 6 grids.
    const cardData = Array.from({ length: 24 }, (_, i) => ({
        id: i + 1,
        title: `Card ${i + 1}`,
    }))

    // Desktop settings: 6 columns (3 slides of 2x2 grids), with zoom
    const desktopSettings = {
        centerMode: true,
        dots: false,
        arrows: false,
        infinite: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        centerPadding: "0px",
        beforeChange: (_, next) => setCurrentSlide(next),
        className: "center-slider",
    }

    // Mobile settings: 3 columns, single row, with zoom
    const mobileSettings = {
        centerMode: true,
        dots: false,
        arrows: false,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        centerPadding: "25%",
        beforeChange: (_, next) => setCurrentSlide(next),
        className: "center-slider",
    }

    const totalSlides = isDesktop
        ? multipleRows
            ? chunk(items, 4).length
            : items.length
        : items.length

    const baseSettings = isDesktop ? desktopSettings : mobileSettings

    // Four or fewer items chunk down to a single slide. Stacking them in a 2x2
    // grid inside a third of the carousel leaves cropped cards next to a lot of
    // empty space, so the lone slide takes the full width and lays its items out
    // in one row instead.
    const isSingleGridSlide = isDesktop && multipleRows && totalSlides === 1

    const slidesToShow = isSingleGridSlide ? 1 : baseSettings.slidesToShow

    // react-slick still renders `infinite` clones when there are fewer slides
    // than `slidesToShow`, but sizes the track for the real slides only. The
    // clones then overflow the track and wrap into a vertical stack. Looping
    // and centering are meaningless with nothing to scroll to, so turn both off
    // until there are enough slides to fill a view.
    const hasEnoughSlides = totalSlides > slidesToShow

    const settings = {
        ...baseSettings,
        slidesToShow,
        infinite: hasEnoughSlides,
        centerMode: hasEnoughSlides && baseSettings.centerMode,
    }

    const slides = isDesktop
        ? multipleRows
            ? chunk(items, 4).map((slideItems, idx) =>
                  isSingleGridSlide ? (
                      <div
                          key={idx}
                          className="!flex justify-center gap-6 w-full px-2"
                      >
                          {slideItems.map((item) => (
                              <div key={item.id} className="w-1/4">
                                  <ItemTemplate data={item} />
                              </div>
                          ))}
                      </div>
                  ) : (
                      <div
                          key={idx}
                          className="!grid grid-cols-2 grid-rows-2 gap-2 w-full px-1"
                      >
                          {slideItems.map((item) => (
                              <ItemTemplate key={item.id} data={item} />
                          ))}
                      </div>
                  )
              )
            : items.map((card) => (
                  <div key={card.id} className="px-2">
                      <ItemTemplate data={card} />
                  </div>
              ))
        : items.map((card) => (
              <div key={card.id} className="">
                  <ItemTemplate data={card} />
              </div>
          ))

    return (
        <div className="md:max-w-[1100px] xl:max-w-7xl mx-auto">
            <div
                className={`relative carousel ${
                    hasEnoughSlides ? "" : "carousel--static"
                }`}
            >
                <Slider ref={sliderRef} {...settings}>
                    {slides}
                </Slider>

                {/* Arrows and dots only navigate between slides, so they're
                    dropped entirely when every slide is already on screen. */}
                {hasEnoughSlides && (
                    <>
                        {/* Custom Previous Button */}
                        <button
                            onClick={() => sliderRef.current?.slickPrev()}
                            className="absolute z-10 p-2 transition-colors -translate-y-1/2 bg-white rounded-full shadow-lg left-3 top-1/2 lg:-translate-x-12 lg:border-2 lg:shadow-none hover:bg-gray-100"
                            aria-label="Previous slide"
                        >
                            <ChevronLeft className="w-6 h-6 text-gray-800 lg:w-8 lg:h-8" />
                        </button>

                        {/* Custom Next Button */}
                        <button
                            onClick={() => sliderRef.current?.slickNext()}
                            className="absolute z-10 p-2 transition-colors -translate-y-1/2 bg-white rounded-full shadow-lg right-3 top-1/2 lg:translate-x-12 lg:border-2 lg:shadow-none hover:bg-gray-100"
                            aria-label="Next slide"
                        >
                            <ChevronRight className="w-6 h-6 text-gray-800 lg:w-8 lg:h-8" />
                        </button>
                    </>
                )}
            </div>

            {/* Custom Dots */}
            {hasEnoughSlides && (
                <div className="flex items-center justify-center gap-2">
                    {Array.from({ length: totalSlides }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => sliderRef.current?.slickGoTo(index)}
                            className={`h-2 rounded-full transition-all ${
                                index === currentSlide
                                    ? "w-6 bg-[#C9A876]"
                                    : "w-2 bg-[#C9A876]"
                            }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
